import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, ExternalLink, ArrowRight } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DisclaimerBanner } from "../components/DisclaimerBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function TaperTool() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [benzos, setBenzos] = useState([]);
  const [benzo, setBenzo] = useState("");
  const [dose, setDose] = useState("");
  const [plan, setPlan] = useState(null);

  useEffect(() => { api.get("/taper/benzos").then(({ data }) => setBenzos(data.benzos)).catch(() => {}); }, []);

  const openGpLetter = () => {
    if (!user) {
      toast.info("Create a free account to generate and save your GP letter.");
      navigate("/auth");
      return;
    }
    navigate("/app/taper/letter");
  };

  const generate = async () => {
    if (!benzo || !dose) { toast.error("Please choose a medication and enter your daily dose."); return; }
    try {
      const { data } = await api.get("/taper/plan", { params: { benzo, dose: parseFloat(dose) } });
      setPlan(data);
      localStorage.setItem("tp_plan", JSON.stringify({ ...data, dose }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const chartData = plan?.schedule.map((s) => ({ name: `S${s.step}`, mg: s.to_mg })) || [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Taper Education Tool</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">A starting point to discuss with your GP or prescriber — based on <a href="https://benzo.org.uk/manual" target="_blank" rel="noreferrer" className="text-[#4A7c59] underline">The Ashton Manual</a>. This is not a prescription.</p>
      </div>
      <DisclaimerBanner />

      <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
        <div className="grid gap-5 sm:grid-cols-3 sm:items-end">
          <div>
            <Label>Your benzodiazepine</Label>
            <Select value={benzo} onValueChange={setBenzo}>
              <SelectTrigger data-testid="select-benzo" className="mt-1 rounded-xl py-6"><SelectValue placeholder="Choose medication" /></SelectTrigger>
              <SelectContent>{benzos.map((b) => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Current daily dose (mg)</Label>
            <Input data-testid="input-dose" type="number" value={dose} onChange={(e) => setDose(e.target.value)} placeholder="e.g. 10" className="mt-1 rounded-xl py-6" />
          </div>
          <Button data-testid="generate-plan" onClick={generate} className="rounded-full bg-[#4A7c59] py-6 hover:bg-[#3B6347]">Show reference plan</Button>
        </div>
      </div>

      {plan && (
        <div className="space-y-6 animate-fade-up">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl bg-[#4A7c59]/10 p-6"><div className="text-sm text-muted-foreground">Diazepam equivalent</div><div className="font-head text-3xl font-bold text-[#4A7c59]" data-testid="diazepam-eq">{plan.diazepam_equivalent} mg</div></div>
            <div className="rounded-2xl bg-[#8FB8DE]/15 p-6"><div className="text-sm text-muted-foreground">Reference taper length</div><div className="font-head text-3xl font-bold text-[#2C4763]">~{plan.estimated_weeks} weeks</div></div>
            <div className="rounded-2xl bg-[#E8907E]/15 p-6"><div className="text-sm text-muted-foreground">Half-life</div><div className="font-head text-xl font-bold text-[#8a4436]">{plan.benzo.half_life}</div></div>
          </div>

          <Tabs defaultValue="schedule">
            <TabsList className="rounded-full bg-muted p-1">
              <TabsTrigger value="schedule" data-testid="tab-schedule" className="rounded-full">Taper schedule</TabsTrigger>
              <TabsTrigger value="equiv" data-testid="tab-equiv" className="rounded-full">Equivalents</TabsTrigger>
              <TabsTrigger value="symptoms" data-testid="tab-symptoms" className="rounded-full">Withdrawal stages</TabsTrigger>
            </TabsList>

            <TabsContent value="schedule" className="mt-4 space-y-4">
              <div className="rounded-2xl border border-border bg-white p-6">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} unit="mg" />
                    <Tooltip /><Line type="monotone" dataKey="mg" stroke="#4A7c59" strokeWidth={3} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-hidden rounded-2xl border border-border bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-left text-muted-foreground"><tr><th className="p-3">Step</th><th className="p-3">Timing</th><th className="p-3">From → To (diazepam eq.)</th><th className="p-3">Note</th></tr></thead>
                  <tbody>{plan.schedule.map((s) => (
                    <tr key={s.step} className="border-t border-border"><td className="p-3 font-semibold">{s.step}</td><td className="p-3">{s.weeks}</td><td className="p-3">{s.from_mg} → {s.to_mg} mg <span className="text-muted-foreground">(-{s.reduction_mg})</span></td><td className="p-3 text-muted-foreground">{s.note || "—"}</td></tr>
                  ))}</tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">Reductions of roughly 10% with hold periods, per Ashton Manual principles. Your prescriber may adjust the pace to suit you.</p>
            </TabsContent>

            <TabsContent value="equiv" className="mt-4">
              <div className="overflow-hidden rounded-2xl border border-border bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-left text-muted-foreground"><tr><th className="p-3">Medication</th><th className="p-3">≈ 10mg diazepam</th><th className="p-3">Half-life</th></tr></thead>
                  <tbody>{plan.equivalence_table.map((b) => (
                    <tr key={b.key} className={`border-t border-border ${b.key === plan.benzo.key ? "bg-[#4A7c59]/5 font-medium" : ""}`}><td className="p-3">{b.name}</td><td className="p-3">{b.eq10} mg</td><td className="p-3 text-muted-foreground">{b.half_life}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="symptoms" className="mt-4 grid gap-4 sm:grid-cols-2">
              {plan.withdrawal_stages.map((st) => (
                <div key={st.stage} className="rounded-2xl border border-border bg-white p-6">
                  <h4 className="font-head font-semibold text-[#2C363F]">{st.stage}</h4>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">{st.symptoms.map((s) => <li key={s} className="flex gap-2"><span className="text-[#4A7c59]">•</span>{s}</li>)}</ul>
                </div>
              ))}
            </TabsContent>
          </Tabs>

          <div className="flex flex-wrap gap-3">
            <Button data-testid="gp-letter-btn" onClick={openGpLetter} className="rounded-full bg-[#4A7c59] py-6 hover:bg-[#3B6347]"><FileText className="mr-2 h-4 w-4" /> Generate GP letter / PDF <ArrowRight className="ml-2 h-4 w-4" /></Button>
            <a href="https://benzo.org.uk/manual" target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full border border-border bg-white px-6 py-3 text-sm font-medium text-[#4A7c59]"><ExternalLink className="h-4 w-4" /> Read the Ashton Manual</a>
          </div>
        </div>
      )}
    </div>
  );
}
